import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import * as d3 from 'd3';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

/**
 * JournalQuartiles
 * - Journal Quartiles 차트 (JCR, Scopus, KCI Q1~Q4)
 * - API: /rm/v1/journal/quartiles
 */
export function JournalQuartiles({ data: externalData = null }) {
  const ref = useRef(null);
  const tooltipRef = useRef(null);
  const [activeTab, setActiveTab] = useState('JCR');

  const [quartilesData, setQuartilesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (externalData !== null && externalData !== undefined) {
      return;
    }

    let alive = true;
    async function fetchQuartiles() {
      setLoading(true);
      setErr(null);
      try {
        const response = await get('/rm/v1/journal/quartiles');
        if (alive) {
          if (response?.status && response?.result) {
            setQuartilesData(response.result);
            const r = response.result;
            if (Array.isArray(r.JCR) && r.JCR.length > 0) setActiveTab('JCR');
            else if (Array.isArray(r.Scopus) && r.Scopus.length > 0) setActiveTab('Scopus');
            else if (Array.isArray(r.KCI) && r.KCI.length > 0) setActiveTab('KCI');
          } else {
            setErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setQuartilesData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }
    fetchQuartiles();
    return () => { alive = false; };
  }, [externalData]);

  const tabs = useMemo(() => {
    const src = externalData || quartilesData;
    if (!src || typeof src !== 'object') return [];
    const t = [];
    if (Array.isArray(src.JCR) && src.JCR.length > 0) t.push({ key: 'JCR', label: 'JCR' });
    if (Array.isArray(src.Scopus) && src.Scopus.length > 0) t.push({ key: 'Scopus', label: 'Scopus' });
    if (Array.isArray(src.KCI) && src.KCI.length > 0) t.push({ key: 'KCI', label: 'KCI' });
    return t;
  }, [externalData, quartilesData]);

  const data = useMemo(() => {
    const src = externalData || quartilesData;
    if (!src || typeof src !== 'object') return [];
    const arr = src[activeTab];
    return Array.isArray(arr) ? arr.map(d => ({ label: d.label || '', value: Number(d.value) || 0 })) : [];
  }, [externalData, quartilesData, activeTab]);

  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const lastDrawRef = useRef({ key: '', width: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;
    let tId = 0;
    const measureNow = () => {
      const rect = el.getBoundingClientRect?.();
      const w = (rect?.width ?? el.clientWidth) || 0;
      const h = (rect?.height ?? el.clientHeight) || 0;
      setContainerSize(prev => (prev.width !== w || prev.height !== h ? { width: w, height: h } : prev));
    };
    const measure = () => {
      if (tId) clearTimeout(tId);
      tId = setTimeout(measureNow, 60);
    };
    measure();
    let ro;
    try {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    } catch (e) {
      window.addEventListener('resize', measure);
      requestAnimationFrame(measure);
      return () => {
        window.removeEventListener('resize', measure);
        if (tId) clearTimeout(tId);
      };
    }
    try {
      if (document?.fonts?.ready?.then) document.fonts.ready.then(() => measure());
    } catch (e) { }
    return () => {
      if (tId) clearTimeout(tId);
      try { ro?.disconnect(); } catch (e) { }
    };
  }, []);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const measuredWidth = containerSize.width;
    if (!measuredWidth || measuredWidth <= 0) return;
    if (!Array.isArray(data) || data.length === 0) return;

    const dataKey = data.map(d => `${d.label}:${d.value}`).join('|');
    const drawKey = `${Math.round(measuredWidth)}::${dataKey}`;
    const prev = lastDrawRef.current;
    const widthChanged = Math.abs((prev.width || 0) - measuredWidth) >= 2;
    if (!widthChanged && prev.key === drawKey) return;

    lastDrawRef.current = { key: drawKey, width: measuredWidth };
    container.innerHTML = '';

    const width = measuredWidth;
    const margin = { top: 15, right: 20, bottom: 25, left: 50 };
    const rowHeight = 40;
    const baseHeight = margin.top + margin.bottom + rowHeight * Math.max(data.length, 4);
    const height = Math.max(280, baseHeight);

    const svg = d3.select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const maxVal = d3.max(data, d => d.value) || 1;
    const xMax = Math.max(1, Math.ceil(maxVal * 1.1)); // 최대값의 10% 여유

    const x = d3.scaleLinear().domain([0, xMax]).range([0, innerW]);
    const y = d3.scaleBand().domain(data.map(d => d.label)).range([0, innerH]).padding(0.2);

    const tickCount = width < 350 ? 3 : width < 450 ? 4 : 5;
    const xTicks = x.ticks(tickCount);
    const fontSize = width < 400 ? 11 : 12;

    g.append('g')
      .selectAll('line')
      .data(xTicks)
      .enter()
      .append('line')
      .attr('x1', d => x(d))
      .attr('x2', d => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#E4E8F1');

    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3.axisBottom(x)
          .tickValues(xTicks)
          .tickFormat(d3.format('d')),
      )
      .call(gx => {
        gx.selectAll('.domain').remove();
        gx.selectAll('text')
          .attr('fill', '#8D9AAF')
          .style('font-size', fontSize)
          .style('text-anchor', 'middle');
        gx.selectAll('line').remove();
      });

    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .call(g => {
        g.selectAll('.domain').remove();
        g.selectAll('text')
          .attr('fill', '#6C7687')
          .style('font-size', 12)
          .attr('x', -10)
          .attr('text-anchor', 'end');
      });

    g.selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', 0)
      .attr('y', d => y(d.label))
      .attr('height', y.bandwidth())
      .attr('rx', 4)
      .attr('fill', '#6EA2FF')
      .attr('opacity', 0.9)
      .attr('width', 0)
      .transition()
      .duration(600)
      .attr('width', d => x(d.value));

    const tooltip = d3.select(tooltipRef.current);

    g.selectAll('.hit-area')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'hit-area')
      .attr('x', 0)
      .attr('y', d => y(d.label))
      .attr('height', y.bandwidth())
      .attr('width', innerW)
      .attr('fill', 'transparent')
      .style('cursor', 'default')
      .on('mousemove', function (event, d) {
        const [mx, my] = d3.pointer(event, this);
        tooltip
          .style('display', 'block')
          .style('left', `${margin.left + mx - 60}px`)
          .style('top', `${margin.top + my - 44}px`)
          .html(`<div class="flex items-center gap-2"><span class="inline-block w-[8px] h-[8px] rounded-full bg-[#6EA2FF]"></span><span class="font-semibold text-[#2A2E36]">${d.label}</span><span class="ml-4 text-[#2A2E36]">${d.value}</span></div>`);
      })
      .on('mouseleave', () => tooltip.style('display', 'none'));
  }, [data, containerSize.width]);

  const hasData = Array.isArray(data) && data.length > 0;
  const isJournalsPage = typeof window !== 'undefined' && window.location.pathname.includes('/journals');

  return (
    <>
      <div className="relative min-h-[280px]">
        {isJournalsPage && tabs.length > 0 && (
          <ul className="mb-0 p-[6px] bg-[#F3F5F9] rounded-lg z-10 text-[#8D9AAF] inline-flex text-[15px] gap-[5px]">
            {tabs.map(t => (
              <li
                key={t.key}
                className={`${activeTab === t.key ? 'active bg-white text-[#1F2937] ' : ''}w-[104px] text-center leading-[38px] hover:bg-white rounded-lg cursor-pointer transition-colors`}
                onClick={() => setActiveTab(t.key)}
                data-value={t.key}
              >
                {t.label}
              </li>
            ))}
          </ul>
        )}

        <div ref={ref} className="w-full" />
        <ChartSkeletonLoader show={externalData === null && loading} />
        {externalData === null && err && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-[#F3652D] text-sm">
            {err}
          </div>
        )}
        {!hasData && !loading && !err && (
          <div className="absolute inset-0 flex items-center justify-center text-[#8D9AAF]">
            데이터가 없습니다.
          </div>
        )}
        <div
          ref={tooltipRef}
          className="absolute z-10 hidden px-3 py-2 bg-white border border-[#E4E8F1] rounded-xl shadow-sm text-[13px]"
          style={{ display: 'none' }}
        />
      </div>
    </>
  );
}

export default JournalQuartiles;
